import { GameEventType } from '../interfaces/game-details';

/**
 * Тип события в протоколе ↔ `MatchEventType` бэкенда.
 *
 * Бэкенд отдаёт и принимает типы заглавными (`PENALTY`, `OWN_GOAL`, `TIME_OUT`), а в `GameEventType`
 * половина значений строчные (`'penalty'`, `'owngoal'`, `'timeout'`). Раньше тип из ответа клался в
 * событие как есть, и при следующем `upsertMatch` `mapEventTypeForBackend` не узнавал `'PENALTY'` и
 * отправлял `GOAL` — добавление любого события пересохраняло все остальные голами.
 */
const BACKEND_BY_EVENT_TYPE: Record<GameEventType, string> = {
  [GameEventType.GOAL]: 'GOAL',
  // «Пас и гол» — это гол с `secondaryUserId`; отдельного типа на бэкенде нет.
  [GameEventType.PASS_AND_GOAL]: 'GOAL',
  [GameEventType.YELLOW_CARD]: 'YELLOW_CARD',
  [GameEventType.RED_CARD]: 'RED_CARD',
  [GameEventType.FOUL]: 'FOUL',
  [GameEventType.PENALTY]: 'PENALTY',
  [GameEventType.SAVE]: 'SAVE',
  [GameEventType.TIMEOUT]: 'TIME_OUT',
  [GameEventType.REPLACEMENT]: 'REPLACEMENT',
  [GameEventType.OWN_GOAL]: 'OWN_GOAL',
  [GameEventType.CORNER]: 'CORNER',
  [GameEventType.AFTER_MATCH_PENALTY_GOAL]: 'AFTER_MATCH_PENALTY_GOAL',
  [GameEventType.AFTER_MATCH_PENALTY_MISSED]: 'AFTER_MATCH_PENALTY_MISSED',
  [GameEventType.DEFAULT]: 'GOAL',
};

const EVENT_TYPE_BY_BACKEND: Record<string, GameEventType> = Object.fromEntries(
  (Object.entries(BACKEND_BY_EVENT_TYPE) as [GameEventType, string][])
    .filter(([eventType]) => eventType !== GameEventType.PASS_AND_GOAL && eventType !== GameEventType.DEFAULT)
    .map(([eventType, backendType]) => [backendType, eventType]),
);

function isGameEventType(type: string): type is GameEventType {
  return Object.prototype.hasOwnProperty.call(BACKEND_BY_EVENT_TYPE, type);
}

/**
 * Тип из ответа бэкенда → `GameEventType`. Тип, которого в протоколе нет (`PENALTY_MISSED`, `KILLS`
 * — их пишет трансляция), остаётся как пришёл: `mapEventTypeForBackend` вернёт его без изменений,
 * и пересохранение матча не превратит чужое событие в гол.
 */
export function mapEventTypeFromBackend(type: string): GameEventType {
  return EVENT_TYPE_BY_BACKEND[type] ?? (type as GameEventType);
}

export function mapEventTypeForBackend(type: GameEventType | string): string {
  if (isGameEventType(type)) {
    return BACKEND_BY_EVENT_TYPE[type];
  }

  return type;
}
